import React from 'react';
import { translateLeaveStatus } from '../../../../../utils';
import { Claim, Benefit, Task } from '../../../../../data-models';
import dayjs from 'dayjs';
import { dateFormat } from './shared-styles';
import { CardItemContainer } from '../../components/tw-components';
import {
  TextBase,
  HeaderTextMedium,
  StatusIndicator,
  HighlightNote,
  MobileContainerSM,
  DesktopContainerSM,
} from '../../../tw-components';

interface IClaimCardItem {
  claimItem: Claim;
  ERTasks: Array<Task>;
  EETasks: Array<Task>;
}

function getBenefitStageDisplay(claimItem: Claim, benefit: Benefit): string {
  const status = claimItem.status?.toLowerCase();
  if (!status) {
    return '';
  } else if (status !== 'closed') {
    return benefit.approvedThruDate ? 'Approved' : 'Pending';
  } else {
    return 'Closed';
  }
}

const ClaimsCardItems: (props: {
  claims: Array<Claim>;
  ERTasks: Array<Task>;
  EETasks: Array<Task>;
}) => React.ReactElement = (props) => {
  const { claims } = props;

  return (
    <>
      <div className="tw-mb-3">
        <HighlightNote>
          The dates below may change based on the information provided by your
          medical provider or term of your policy.
        </HighlightNote>
      </div>
      {claims?.map((claimItem: Claim, i: number) =>
        claimItem?.benefits && claimItem?.benefits?.length > 0 ? (
          claimItem.benefits.map((benefit) => (
            <CardItemContainer key={`${benefit.benefitId}-item-${i}`}>
              <BenefitCard claimItem={claimItem} benefit={benefit} />
            </CardItemContainer>
          ))
        ) : (
          <CardItemContainer key={`${claimItem.claimId}-item-${i}`}>
            <PreFileClaimCard
              claimItem={claimItem}
              ERTasks={props.ERTasks}
              EETasks={props.EETasks}
            />
          </CardItemContainer>
        ),
      )}
      <div className="tw-mt-3">
        <HighlightNote>
          The time between your date of disability and the benefit start date is
          not paid by the Disability Benefits policy.
        </HighlightNote>
      </div>
    </>
  );
};

export const BenefitCard = ({
  benefit,
  claimItem,
}: {
  benefit: Benefit;
  claimItem: Claim;
}) => {
  let mappedPaidBy;
  const adminType = benefit.administrationType?.toLowerCase();

  if (adminType === 'aso - advice to pay') {
    mappedPaidBy = 'By Employer';
  } else if (adminType === 'aso - full banking') {
    mappedPaidBy = 'By Unum';
  } else if (adminType === 'fully insured') {
    mappedPaidBy = 'Yes';
  } else if (adminType === 'state pfml approval verifcation') {
    mappedPaidBy = '';
  } else if (benefit.checkCutting === 'Check Cutting') {
    mappedPaidBy = 'By Unum';
  } else if (benefit.checkCutting === 'Non Check Cutting') {
    mappedPaidBy = 'By Employer';
  }

  const statusName = getBenefitStageDisplay(claimItem, benefit);
  const benefitStatusConfig = translateLeaveStatus(statusName);

  return (
    <div className="tw-flex tw-flex-col tw-w-full">
      {/* Mobile View */}
      <MobileContainerSM>
        <div className="tw-flex tw-flex-col tw-w-full tw-gap-2">
          <div className="tw-flex tw-flex-row tw-w-full tw-gap-2">
            <HeaderTextMedium>Plan name</HeaderTextMedium>
            <TextBase>{claimItem.claimType}</TextBase>
          </div>
          <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center">
            <div className="tw-flex tw-flex-row tw-w-1/2 tw-gap-2 tw-items-center">
              {mappedPaidBy ? (
                <>
                  <HeaderTextMedium>Payable if approved?</HeaderTextMedium>
                  <TextBase>{mappedPaidBy}</TextBase>
                </>
              ) : null}
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-w-full sm:tw-w-1/2 tw-items-center tw-justify-end">
              <div className="tw-flex tw-flex-row tw-gap-2">
                <HeaderTextMedium align="center">Status</HeaderTextMedium>
              </div>
              <div className="tw-flex tw-flex-row tw-justify-end">
                <StatusIndicator
                  background={benefitStatusConfig.backgroundColor}
                  color={benefitStatusConfig.color}
                >
                  {statusName}
                </StatusIndicator>
              </div>
            </div>
          </div>
        </div>
      </MobileContainerSM>

      {/* Desktop View */}
      <DesktopContainerSM className="tw-justify-between tw-items-center">
        <div className="tw-flex md:tw-flex-row tw-w-full tw-gap-2">
          <div className="tw-flex tw-flex-col tw-w-1/2 tw-gap-2">
            <HeaderTextMedium>Plan name</HeaderTextMedium>
            <TextBase>{claimItem.claimType}</TextBase>
          </div>
          <div className="tw-flex tw-flex-col tw-w-1/4 tw-gap-2">
            <HeaderTextMedium>Payable if approved?</HeaderTextMedium>
            <TextBase>{mappedPaidBy}</TextBase>
          </div>
          <div className="tw-w-1/4 tw-text-center">
            <HeaderTextMedium align="center">Status</HeaderTextMedium>
            <StatusIndicator
              background={benefitStatusConfig.backgroundColor}
              color={benefitStatusConfig.color}
            >
              {statusName}
            </StatusIndicator>
          </div>
        </div>
      </DesktopContainerSM>

      <div
        className="tw-px-3 tw-my-3 tw-w-full tw-mb-3 last:tw-mb-0
                    tw-border-b tw-border-solid tw-border-unm-interactive01-400 tw-border-0 tw-pb-2 last:tw-pb-0 last:tw-border-b-0"
      ></div>

      <div className="tw-flex md:tw-flex-row tw-w-full tw-gap-2 sm:tw-gap-0 tw-items-center tw-self-end">
        <div className="tw-flex tw-flex-col tw-w-1/3 tw-gap-2">
          <HeaderTextMedium>Date of disability</HeaderTextMedium>
          <TextBase>
            {dayjs(benefit.benefitIncurredDate).format(dateFormat)}
          </TextBase>
        </div>
        <div className="tw-flex tw-flex-col tw-w-1/3 tw-gap-2">
          <HeaderTextMedium>Benefit start date</HeaderTextMedium>
          <TextBase>
            {dayjs(benefit.benefitStartDate).format(dateFormat)}
          </TextBase>
        </div>
        <div className="tw-flex tw-flex-col tw-w-1/3 tw-gap-2">
          {benefit.approvedThruDate && benefit.approvedThruDate.trim() !== '' && (
            <>
              <HeaderTextMedium>Approved through date</HeaderTextMedium>
              <TextBase>
                {dayjs(benefit.approvedThruDate).format(dateFormat)}
              </TextBase>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const PreFileClaimCard: (props: IClaimCardItem) => React.ReactElement = (
  props,
) => {
  const { claimItem } = props;
  const createdDate = claimItem?.createdDate ?? null;
  const statusName = getBenefitStageDisplay(claimItem, {});
  const benefitStatusConfig = translateLeaveStatus(statusName);

  return (
    <div className="tw-flex tw-flex-col tw-w-full">
      {/* Mobile View */}
      <MobileContainerSM>
        <div className="tw-flex tw-flex-col tw-w-full tw-gap-2">
          <div className="tw-flex tw-flex-row tw-w-full tw-gap-2">
            <HeaderTextMedium>Plan name</HeaderTextMedium>
            <TextBase>{claimItem.claimType}</TextBase>
          </div>
          <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center">
            <div className="tw-flex tw-flex-row tw-w-1/2 tw-gap-2 tw-items-center">
              <HeaderTextMedium>Date created</HeaderTextMedium>
              {dayjs(createdDate).isValid() ? (
                <TextBase>{dayjs(createdDate).format(dateFormat)}</TextBase>
              ) : null}
            </div>
            <div className="tw-flex tw-gap-2 tw-w-1/2 tw-items-center tw-justify-end">
              <div className="tw-flex tw-flex-row tw-gap-2">
                <HeaderTextMedium align="center">Status</HeaderTextMedium>
              </div>
              <div className="tw-flex tw-flex-row tw-justify-end">
                <StatusIndicator
                  background={benefitStatusConfig.backgroundColor}
                  color={benefitStatusConfig.color}
                >
                  {statusName}
                </StatusIndicator>
              </div>
            </div>
          </div>
        </div>
      </MobileContainerSM>

      {/* Desktop View */}
      <DesktopContainerSM className="tw-justify-between tw-items-center">
        <div className="tw-flex md:tw-flex-row tw-w-full tw-gap-2">
          <div className="tw-flex tw-flex-col tw-w-1/2 tw-gap-2">
            <HeaderTextMedium>Plan name</HeaderTextMedium>
            <TextBase>{claimItem.claimType}</TextBase>
          </div>
          <div className="tw-flex tw-flex-col tw-w-1/4 tw-gap-2">
            <HeaderTextMedium>Date created</HeaderTextMedium>
            {dayjs(createdDate).isValid() ? (
              <TextBase>{dayjs(createdDate).format(dateFormat)}</TextBase>
            ) : null}
          </div>
          <div className="tw-w-1/4 tw-text-center">
            <HeaderTextMedium align="center">Status</HeaderTextMedium>
            <StatusIndicator
              background={benefitStatusConfig.backgroundColor}
              color={benefitStatusConfig.color}
            >
              {statusName}
            </StatusIndicator>
          </div>
        </div>
      </DesktopContainerSM>
    </div>
  );
};

export default ClaimsCardItems;
